<template>
  <div class="area-detail-container">
    <div class="detail-header">
      <div class="header-title">{{ $t('区域详情') }}</div>
      <a-icon type="close" class="close-icon" @click="handleClose" />
    </div>

    <div v-if="loading" class="loading-container">
      <a-spin :tip="$t('加载中...')" />
    </div>

    <div v-else class="content-container">
      <!-- 区域详情表单 -->
      <div class="detail-form">
        <a-form :colon="false" layout="vertical">
          <a-row :gutter="16">
            <!-- 第一行：名称和编码 -->
            <a-col :span="12">
              <a-form-item :label="$t('区域名称')">
                <div class="form-value">{{ areaInfo.name || '-' }}</div>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item :label="$t('区域编码')">
                <div class="form-value">{{ areaInfo.code || '-' }}</div>
              </a-form-item>
            </a-col>

            <!-- 第二行：片区中心和片区颜色 -->
            <a-col :span="12">
              <a-form-item :label="$t('片区中心')">
                <div class="form-value">{{ areaInfo.areaCenter || '-' }}</div>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item :label="$t('片区颜色')">
                <div class="form-value">
                  <span
                    v-if="areaInfo.areaColour"
                    class="color-block"
                    :style="{ backgroundColor: areaInfo.areaColour }"
                  ></span>
                  {{ areaInfo.areaColour || '-' }}
                </div>
              </a-form-item>
            </a-col>

            <!-- 第三行：片区面积和管网长度 -->
            <a-col :span="12">
              <a-form-item :label="$t('片区面积')">
                <div class="form-value">{{ areaInfo.areaExtent ? `${areaInfo.areaExtent} ㎡` : '-' }}</div>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item :label="$t('管网长度')">
                <div class="form-value">
                  {{ areaInfo.areaPipelineLength ? `${areaInfo.areaPipelineLength} m` : '-' }}
                </div>
              </a-form-item>
            </a-col>

            <!-- 第四行：用户数量和平均压力 -->
            <a-col :span="12">
              <a-form-item :label="$t('用户数量')">
                <div class="form-value">{{ areaInfo.customerCount || '-' }}</div>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item :label="$t('平均压力')">
                <div class="form-value">{{ areaInfo.averagePressure ? `${areaInfo.averagePressure} kPa` : '-' }}</div>
              </a-form-item>
            </a-col>

            <!-- 第五行：管理员和电话 -->
            <a-col :span="12">
              <a-form-item :label="$t('管理员')">
                <div class="form-value">{{ areaInfo.adminName || '-' }}</div>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item :label="$t('电话')">
                <div class="form-value">{{ areaInfo.adminPhone || '-' }}</div>
              </a-form-item>
            </a-col>

            <!-- 第六行：创建时间 -->
            <a-col :span="24">
              <a-form-item :label="$t('创建时间')">
                <div class="form-value">{{ areaInfo.createTime || '-' }}</div>
              </a-form-item>
            </a-col>

            <!-- 第七行：备注（独占一行） -->
            <a-col :span="24">
              <a-form-item :label="$t('备注')">
                <div class="form-value">{{ areaInfo.remark || '-' }}</div>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <!-- 节点列表 -->
      <div class="node-list-section">
        <div class="node-list-header">
          <div class="node-list-title">{{ $t('区域节点列表') }}</div>
          <a-button type="primary" size="small" class="add-node-btn" @click="$emit('add-nodes')"> {{ $t('新增节点') }} </a-button>
        </div>

        <div class="node-list-container">
          <a-table
            :columns="columns"
            :data-source="nodeList"
            :pagination="false"
            size="small"
            rowKey="id"
            :scroll="{ y: 310 }"
          >
            <template slot="operation" slot-scope="text, record">
              <a-popconfirm :title="$t('确定删除此节点吗?')" @confirm="() => $emit('delete-node', record.id)">
                <a-button type="link" style="color: #ff4d4f"> {{ $t('删除') }} </a-button>
              </a-popconfirm>
            </template>
          </a-table>
        </div>
      </div>

      <!-- 关联行政区域 -->
      <div class="relate-area-section">
        <a-button
          type="primary"
          size="small"
          class="relate-area-btn"
          :loading="boundCountLoading"
          @click="$emit('relate-area')"
        >
          {{ $t('关联行政区域') }}<span v-if="boundCount !== null"> ({{ boundCount }})</span>
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import { filterDictTextByCache } from '@comp/dict/JDictSelectUtil'
import { getAction } from '@/api/manage'

export default {
  name: 'AreaDetail',
  props: {
    areaInfo: {
      type: Object,
      default: () => ({}),
    },
    nodeList: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
  },
  data() {
    return {
      filterDictTextByCache: filterDictTextByCache,
      boundCount: null, // 已关联行政区域数量
      boundCountLoading: false,
    }
  },
  computed: {
    columns() {
      return [
        { title: this.$t('编码'), dataIndex: 'code', width: 40 },
        {
          title: this.$t('类型'),
          dataIndex: 'type',
          width: 60,
          customRender: text => filterDictTextByCache('node_type', text),
        },
        { title: this.$t('名称'), dataIndex: 'name', width: 130 },
        {
          title: this.$t('操作'),
          key: 'operation',
          scopedSlots: { customRender: 'operation' },
          width: 40,
        },
      ]
    },
  },
  watch: {
    // GIS区域变化时重新查询已关联数量
    'areaInfo.id': {
      handler(val) {
        if (val) {
          this.loadBoundCount(val)
        } else {
          this.boundCount = null
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleClose() {
      this.$emit('close')
    },
    // 查询当前GIS区域已关联的行政区域数量
    loadBoundCount(tpAreaId) {
      this.boundCountLoading = true
      getAction('/jeecg-charge/meter/meterArea/list', {
        bindTpAreaId: tpAreaId,
        pageNo: 1,
        pageSize: 1,
      })
        .then(res => {
          if (res.success && res.result) {
            // 分页对象返回 total
            this.boundCount = res.result.total != null ? res.result.total : (res.result.records || []).length
          } else {
            this.boundCount = 0
          }
        })
        .catch(() => {
          this.boundCount = 0
        })
        .finally(() => {
          this.boundCountLoading = false
        })
    },
    /**
     * 供父组件在绑定/解绑成功后调用,刷新数量。
     * 父组件: this.$refs.areaDetail.refreshBoundCount()
     */
    refreshBoundCount() {
      if (this.areaInfo && this.areaInfo.id) {
        this.loadBoundCount(this.areaInfo.id)
      }
    },
  },
}
</script>

<style scoped>
.area-detail-container {
  position: absolute;
  top: 0;
  right: 0;
  width: 320px;
  height: 100%;
  background: rgba(31, 31, 31, 0.95);
  border-radius: 6px;
  padding: 15px;
  z-index: 999;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  opacity: 0.75;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  border-bottom: 1px solid #444;
  padding-bottom: 10px;
  margin-bottom: 15px;
  flex-shrink: 0;
}

.header-title {
  flex-grow: 1;
}

.close-icon {
  cursor: pointer;
  font-size: 16px;
  color: #ccc;
  transition: all 0.3s;
}

.close-icon:hover {
  color: #fff;
  transform: scale(1.1);
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.content-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.detail-form {
  flex-shrink: 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 50%;
  margin-bottom: 7px;
}

.detail-form::-webkit-scrollbar {
  width: 5px;
}

.detail-form::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

::v-deep .ant-form-item {
  margin-bottom: 0px;
  padding-bottom: 2px;
}
::v-deep .ant-form-item-label > label {
  color: #aaa;
  font-size: 12px;
}

::v-deep .ant-form-vertical .ant-form-item-label {
  padding: 0px;
}
.form-value {
  color: #fff;
  padding-bottom: 2px;
  font-size: 14px;
}

.color-block {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 8px;
  vertical-align: middle;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.node-list-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(42, 42, 42, 0.6);
  border-radius: 4px;
  padding: 3px;
}

.node-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.node-list-title {
  color: #fff;
  font-weight: bold;
  font-size: 14px;
}

.add-node-btn {
  background: #1890ff;
  border-color: #1890ff;
  font-size: 12px;
  height: 24px;
  padding: 0 8px;
}

.relate-area-section {
  flex-shrink: 0;
  margin-top: 10px;
  text-align: center;
}

.relate-area-btn {
  background: #1890ff;
  border-color: #1890ff;
  font-size: 12px;
  height: 28px;
  width: 100%;
}

/* 优化表格行间距和操作列样式 */
.node-list-container ::v-deep .ant-table-small .ant-table-thead > tr > th,
.node-list-container ::v-deep .ant-table-small .ant-table-tbody > tr > td {
  padding: 3px 3px !important; /* 增加行间距 */
}

/* 操作按钮样式 */
.node-list-container ::v-deep .ant-table-small .ant-table-tbody .ant-btn {
  padding: 0 4px;
  height: auto;
  line-height: 1;
}

/* 表格行间距调整 */
::v-deep .ant-table-small > .ant-table-content > .ant-table-body {
  margin: 0;
}

::v-deep .ant-table {
  background: transparent;
}

::v-deep .ant-table-thead > tr > th {
  background: rgba(42, 42, 42, 0.8);
  color: #aaa;
  border-bottom: 1px solid #444;
  font-size: 12px;
}

::v-deep .ant-table-tbody > tr > td {
  background: rgba(31, 31, 31, 0.6);
  color: #ddd;
  border-bottom: 1px solid #444;
  font-size: 12px;
  padding: 12px 12px; /* 增加单元格内边距 */
  height: 40px; /* 固定行高 */
}

/* 行悬停效果 */
::v-deep .ant-table-row:hover > td {
  background: rgba(51, 51, 51, 0.8) !important;
}

/* 滚动条样式 */
::v-deep .ant-table-body::-webkit-scrollbar {
  width: 6px;
}

::v-deep .ant-table-body::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.7);
  border-radius: 3px;
}

::v-deep .ant-table-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 1);
  border-radius: 3px;
}

::v-deep .ant-table-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 1);
}
</style>
